// features/workstation-chat-panel.js — WORKSTATION CHAT PANEL
// ═══════════════════════════════════════════════════════════
// Layer 1 chat panel for the workstation. Uses useChatThread
// directly for messages + scroll engine + SSE/JSON streaming +
// typing animation + abort.
//
// Extracted sub-modules:
//   hooks/use-workstation-chat.js   — beforeSend, buildRequest,
//                                     onTextToken, onResponseDone,
//                                     message load effect, health
//                                     check effect, action handlers
//   components/workstation-message.js  — WorkstationMessage + fence cards
//   components/workstation-chat-cards.js — parked/health/chips/canvas cards
//   components/workstation-slash-commands.js — SlashCommandPicker
// ═══════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react';
import ChatInputBar from '../components/chat-input-bar';
import HighlightToolbar from '../components/highlight-toolbar';
import { useWorkstationChat } from '../hooks/use-workstation-chat';
import { useVoice } from '../hooks/use-voice';
import { useArtifacts } from '../hooks/use-artifacts';
import WorkstationRoleBar, { buildSoloParticipants } from './workstation-role-bar';
import SlashCommandPicker from '../components/workstation-slash-commands';
import { WorkstationMessage } from '../components/workstation-message';
import {
  ParkedSummaryCard,
  HealthReminderCard,
  ChipsRow,
  CanvasSuggestionCard,
} from '../components/workstation-chat-cards';

export default function WorkstationChatPanel({
  userId,
  supabase,
  userRecord,
  workspaceId,
  workspaceSession,
  workspaceGoal,
  voiceConfig,         // optional — { session, threadHistory, voiceEnabled, onVoiceToggle }
  // Optional: when set, messages are stored under this session
  // id instead of workspaceId. Used by the new Workstation
  // Think tab to maintain multiple chat threads on top of the
  // same workspace. Solo Space passes nothing → behavior
  // unchanged (sessionId == workspaceId).
  chatSessionIdOverride = null,
  // Optional: when true, hides the WorkstationRoleBar (the
  // Tim/Emily participant header at the top of the chat
  // panel). Used by the Workstation Think tab — that surface
  // is always Solo, so the participant strip is dead weight.
  // Default false → Solo Space behavior unchanged.
  hideParticipants = false,
  // Optional: skip the auto-greeting on empty threads (Think
  // tab uses true). See useWorkstationChat for details.
  skipOpener = false,
  createMomentNode,
  createFileNode,
  filesAtLimit,
  onThinking,
  onArtifactCreated,
  focusedArtifact,
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [chips, setChips] = useState([]);
  const [canvasSuggestion, setCanvasSuggestion] = useState(null);
  const [showSlashPicker, setShowSlashPicker] = useState(false);
  const [slashQuery, setSlashQuery] = useState('');
  const [deepSearchActive, setDeepSearchActive] = useState(false);
  const [deepModeArmed, setDeepModeArmed] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [limitWarning, setLimitWarning] = useState(null);
  const [parkedSummary, setParkedSummary] = useState(
    workspaceSession?.workspace_metadata?.parked_summary || null
  );
  const [parkedAt, setParkedAt] = useState(
    workspaceSession?.workspace_metadata?.parked_at || null
  );
  const [staleNodes, setStaleNodes] = useState([]);
  const inputRef = useRef(null);
  const voiceSourceRef = useRef(false);
  const voiceResponseCallbackRef = useRef(null);

  const { saveArtifact } = useArtifacts({ userId, supabase });

  const {
    chat,
    clearPark,
    handleHealthAck,
    handleRegenerate,
    handleEditMessage,
    handlePinMessage,
  } = useWorkstationChat({
    userId, supabase, userRecord, workspaceId, workspaceSession, workspaceGoal,
    chatSessionIdOverride,
    skipOpener,
    messages, setMessages,
    pendingFile, setPendingFile,
    deepSearchActive, setDeepSearchActive,
    deepModeArmed, setDeepModeArmed,
    parkedSummary, setParkedSummary, setParkedAt,
    setLimitWarning, setChips, setCanvasSuggestion,
    setStaleNodes, setLoadingHistory,
    createMomentNode, createFileNode, filesAtLimit,
    onThinking, onArtifactCreated, focusedArtifact,
    inputRef, voiceResponseCallbackRef,
  });

  const { scrollContainerRef, lastUserMsgRef, spacerRef, loading, showScrollBtn, scrollToBottom, send: chatSend, abort } = chat;

  // ── Voice integration (optional) ────────────────────────
  const voice = useVoice({
    session: voiceConfig?.session || null,
    userRecord,
    currentSessionId: chatSessionIdOverride || workspaceId,
    threadHistory: voiceConfig?.threadHistory || [],
    voiceEnabled: voiceConfig?.voiceEnabled ?? true,
    inputRef,
    setInput: (val) => {
      setInput(val);
      if (inputRef?.current) inputRef.current.innerText = val;
    },
    send: () => submitMessage(),
    voiceSourceRef,
    voiceResponseCallbackRef,
  });
  const { voiceListening = false, voiceEnabled = true, toggleVoice = () => {} } = voice || {};

  // Anchor chips to the message that produced them. When chips are set
  // by onResponseDone, capture the messages.length at that moment.
  // When a newer message arrives, messages.length grows past the
  // anchor and the chips clear — they belong to the previous turn.
  const chipsAnchorCountRef = useRef(0);
   
  useEffect(() => {
    if (chips.length > 0) chipsAnchorCountRef.current = messages.length;
  }, [chips]);
   
  useEffect(() => {
    if (chips.length > 0 && messages.length > chipsAnchorCountRef.current) {
      setChips([]);
    }
  }, [messages.length]);

  // ── ⌘1 focus listener ──
  useEffect(() => {
    function onFocusChat() {
      if (!inputRef.current) return;
      inputRef.current.focus();
      try {
        const range = document.createRange();
        range.selectNodeContents(inputRef.current);
        range.collapse(false);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      } catch (_) { /* selection API may be restricted */ }
    }
    window.addEventListener('workstation:focus-chat', onFocusChat);
    return () => window.removeEventListener('workstation:focus-chat', onFocusChat);
  }, []);

  // ── Continue Thread listener (from lens findings) ──
  useEffect(() => {
    function onFillInput(e) {
      const text = e?.detail?.text || '';
      if (!text || !inputRef.current) return;
      setInput(text);
      inputRef.current.innerText = text;
      inputRef.current.focus();
      try {
        const range = document.createRange();
        range.selectNodeContents(inputRef.current);
        range.collapse(false);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      } catch (_) { /* selection API may be restricted */ }
    }
    window.addEventListener('workstation:fill-chat-input', onFillInput);
    return () => window.removeEventListener('workstation:fill-chat-input', onFillInput);
  }, []);

  // ── Submit ──────────────────────────────────────────────
  function submitMessage(directText) {
    const text = (directText !== undefined ? directText : input).trim();
    if (!text && !pendingFile) return;
    setChips([]);
    chatSend(text);
    setInput('');
    if (inputRef.current) inputRef.current.innerText = '';
  }

  // ── Slash command detection ─────────────────────────────
  function handleInputChange(val) {
    const trimmed = (val || '').trim();
    if (trimmed.startsWith('/') && trimmed.length > 1) {
      setSlashQuery(trimmed.slice(1).toLowerCase());
      setShowSlashPicker(true);
    } else {
      setShowSlashPicker(false);
      setSlashQuery('');
    }
  }

  function handleSlashSelect(cmd) {
    setShowSlashPicker(false);
    setSlashQuery('');
    const prompts = {
      canvas: 'Build on the canvas: ',
      compare: 'Compare these two options side by side: ',
      summarize: 'Summarize our conversation so far — key points, decisions made, and open questions.',
      plan: 'Create an action plan from what we discussed. Include clear next steps, owners, and deadlines.',
      table: 'Organize this into a table on the canvas: ',
      export: 'Export our conversation as a clean document I can share.',
    };
    if (cmd.id === 'reset') {
      setMessages([]); setInput('');
      if (inputRef.current) inputRef.current.innerText = '';
      return;
    }
    if (cmd.id === 'deep') {
      setDeepModeArmed(true); setInput('');
      if (inputRef.current) inputRef.current.innerText = '';
      return;
    }
    if (['summarize', 'plan', 'export'].includes(cmd.id)) {
      setInput('');
      if (inputRef.current) inputRef.current.innerText = '';
      submitMessage(prompts[cmd.id]);
      return;
    }
    const prompt = prompts[cmd.id] || '';
    setInput(prompt);
    if (inputRef.current) {
      inputRef.current.innerText = prompt;
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(inputRef.current);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
      inputRef.current.focus();
    }
  }

  // ── File upload from button or drop ─────────────────────
  async function handleFilePickerSelect({ base64, name, type }) {
    if (filesAtLimit) {
      setLimitWarning('Workspace file limit reached (20). Remove an older file first.');
      setTimeout(() => setLimitWarning(null), 4000);
      return;
    }
    const isImage = type.startsWith('image/');
    const isPDF = type === 'application/pdf';
    if (isImage) {
      const cleanBase64 = base64.includes(',') ? base64.split(',')[1] : base64;
      setPendingFile({ base64: cleanBase64, name, type, isImage: true, isPDF: false });
      const img = new window.Image();
      img.onload = () => {
        const MAX = 1500;
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) {
          if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
          else { w = Math.round(w * MAX / h); h = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        const compressed = canvas.toDataURL('image/jpeg', 0.7);
        setPendingFile({ base64: compressed.split(',')[1], name, type: 'image/jpeg', isImage: true, isPDF: false });
        if (inputRef?.current) inputRef.current.focus();
      };
      img.onerror = () => {
        setPendingFile({ base64: cleanBase64, name, type, isImage: true, isPDF: false });
      };
      img.src = base64.includes(',') ? base64 : `data:${type};base64,${base64}`;
    } else {
      const cleanBase64 = base64.includes(',') ? base64.split(',')[1] : base64;
      setPendingFile({ base64: cleanBase64, name, type, isImage, isPDF });
      if (inputRef?.current) inputRef.current.focus();
    }
  }

  // ── Derived values for role bar ─────────────────────────
  const participants = buildSoloParticipants({
    userId, userRecord, workspaceSession,
    isShiftLoading: loading,
    isUserTyping: input.trim().length > 0,
  });

  const lastSpeakerId = (() => {
    if (loading) return 'shift';
    const lastMsg = messages[messages.length - 1];
    if (!lastMsg) return null;
    return lastMsg.role === 'assistant' ? 'shift' : (lastMsg.sender_id || userId);
  })();

  const lastUserIndex = messages.reduce((acc, m, i) => (m.role === 'user' ? i : acc), -1);

  return (
    <div style={{
      flex: 1, minWidth: 240, background: '#07070a',
      borderRight: '1px solid #1e1e2a',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      {!hideParticipants && (
        <WorkstationRoleBar participants={participants} lastSpeakerId={lastSpeakerId} />
      )}

      {staleNodes.length > 0 && (
        <div style={{ padding: '10px 16px 0 16px', flexShrink: 0 }}>
          <HealthReminderCard nodes={staleNodes} onAck={handleHealthAck} />
        </div>
      )}

      {/* Keyframe animations + scrollbar styles for canvas building indicators */}
      <style jsx global>{`
        @keyframes ws-artifact-building {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        @keyframes ws-artifact-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .ws-chat-scroll { scrollbar-width: thin; scrollbar-color: #1c1c1c transparent; }
        .ws-chat-scroll::-webkit-scrollbar { width: 8px; }
        .ws-chat-scroll::-webkit-scrollbar-track { background: transparent; }
        .ws-chat-scroll::-webkit-scrollbar-thumb { background: #161616; border-radius: 4px; border: 2px solid #0a0a0a; }
        .ws-chat-scroll::-webkit-scrollbar-thumb:hover { background: #2a2a2a; }
      `}</style>

      {/* Message list */}
      <div ref={scrollContainerRef} className="ws-chat-scroll" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <div style={{ padding: '14px 16px 0 16px' }}>
          {parkedSummary && !loadingHistory && (
            <ParkedSummaryCard summary={parkedSummary} parkedAt={parkedAt} onContinue={clearPark} onDismiss={clearPark} />
          )}
          {loadingHistory && (
            <div style={{ color: '#555', fontSize: 11, fontStyle: 'italic', fontFamily: 'Georgia, serif', textAlign: 'center', marginTop: 20 }}>
              Loading…
            </div>
          )}
          {!loadingHistory && messages.length === 0 && (
            <div style={{ color: '#444', fontSize: 12, fontStyle: 'italic', fontFamily: 'Georgia, serif', textAlign: 'center', marginTop: 40, lineHeight: 1.7, padding: '0 20px' }}>
              Start by asking Shift something, or attach a file to discuss.
            </div>
          )}
          {messages.map((m, i) => (
            <WorkstationMessage
              key={m.id || i}
              m={m} i={i}
              isLastUser={i === lastUserIndex}
              lastUserMsgRef={lastUserMsgRef}
              userRecord={userRecord}
              onPin={(msg) => handlePinMessage(msg, saveArtifact, loading)}
              onRegenerate={(msg) => handleRegenerate(msg, chatSend, loading)}
              onEdit={(msg) => handleEditMessage(msg, setInput, inputRef, loading)}
            />
          ))}
          {!loading && <ChipsRow chips={chips} onSelect={(chip) => { setChips([]); submitMessage(chip); }} />}
          {!loading && (
            <CanvasSuggestionCard
              suggestion={canvasSuggestion}
              aiName={userRecord?.ai_name}
              onApprove={() => { setCanvasSuggestion(null); submitMessage(`Yes, go ahead: ${canvasSuggestion}`); }}
              onDismiss={() => setCanvasSuggestion(null)}
            />
          )}
          <div ref={spacerRef} style={{ height: 0, flexShrink: 0 }} aria-hidden="true" />
        </div>
      </div>

      {/* Scroll-to-bottom button */}
      {showScrollBtn && messages.length > 0 && (
        <div style={{ height: 38, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#07070a', borderTop: '1px solid #1e1e2a' }}>
          <button
            onClick={scrollToBottom} title="Scroll to latest"
            style={{ background: '#111118', border: '1px solid #2a2a2a', borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'border-color 0.15s', padding: 0 }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#444'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M8 3v10M3.5 8.5L8 13l4.5-4.5" stroke="#888" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}

      {limitWarning && (
        <div style={{ padding: '8px 14px', background: 'rgba(196,96,80,0.08)', borderTop: '1px solid rgba(196,96,80,0.2)', color: '#c46050', fontSize: 11, fontFamily: 'Georgia, serif', fontStyle: 'italic', flexShrink: 0 }}>
          {limitWarning}
        </div>
      )}

      {/* Slash command picker + input bar */}
      <div style={{ position: 'relative' }}>
        <SlashCommandPicker query={slashQuery} onSelect={handleSlashSelect} visible={showSlashPicker} />
      </div>
      <ChatInputBar
        inputRef={inputRef}
        value={input}
        setValue={setInput}
        onSend={(merged) => submitMessage(merged)}
        loading={loading}
        onAbort={abort}
        pendingFile={pendingFile}
        setPendingFile={setPendingFile}
        onFileSelect={handleFilePickerSelect}
        filesAtLimit={filesAtLimit}
        deepSearchActive={deepSearchActive}
        setDeepSearchActive={setDeepSearchActive}
        deepModeArmed={deepModeArmed}
        setDeepModeArmed={setDeepModeArmed}
        voiceListening={voiceListening}
        voiceEnabled={voiceEnabled}
        onInputChange={handleInputChange}
        onVoiceToggle={() => {
          const next = !voiceEnabled;
          voiceConfig?.onVoiceToggle?.(next);
          if (next) toggleVoice();
          else if (voiceListening) toggleVoice();
        }}
        placeholder="Message…"
      />

      {/* Text-selection toolbar — pops above any selected text in
          the message thread. Highlight / Ideas / Notes save to the
          notes table; Reply quotes the selection into this input. */}
      <HighlightToolbar
        userId={userId}
        supabase={supabase}
        sessionId={chatSessionIdOverride || workspaceId}
        onReply={(text) => {
          const quoted = `"${text}"\n`;
          setInput(quoted);
          if (inputRef.current) {
            inputRef.current.innerText = quoted;
            inputRef.current.focus();
          }
        }}
      />
    </div>
  );
}
